import React, { useState, useEffect } from 'react';
import { COLORS, SPACING } from '../styles/theme';
import { loadOrCreateIdentity, signMessage } from '../lib/identity';
import { getAllReports, dismissReport, isAdmin, ContentReport } from '../lib/moderation';
import { pubkeyShortHash } from '../lib/sanitize';

const RELAY_HTTP = 'https://relay.authenticulture.net';

async function sendTakedown(eventId: string): Promise<boolean> {
  const identity = loadOrCreateIdentity();
  const timestamp = Date.now();
  const sig = signMessage(`takedown:${eventId}:${timestamp}`, identity.privateKey);
  try {
    const res = await fetch(`${RELAY_HTTP}/admin/takedown`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ eventId, adminPubkey: identity.publicKey, timestamp, sig }),
    });
    const json = await res.json();
    return json.ok === true;
  } catch {
    return false;
  }
}

const REASON_LABELS: Record<ContentReport['reason'], string> = {
  spam: 'Spam',
  harassment: 'Harassment',
  illegal: 'Illegal content',
  other: 'Other',
};

export function AdminReports({ onBack }: { onBack: () => void }) {
  const identity = loadOrCreateIdentity();
  const [reports, setReports] = useState<ContentReport[]>(() => getAllReports());
  const [takingDown, setTakingDown] = useState<string | null>(null);
  const [takedownStatus, setTakedownStatus] = useState<Record<string, 'ok' | 'fail'>>({});

  useEffect(() => {
    setReports(getAllReports());
  }, []);

  const handleDismiss = (id: string) => {
    dismissReport(id);
    setReports(getAllReports());
  };

  const handleTakedown = async (report: ContentReport) => {
    setTakingDown(report.id);
    const ok = await sendTakedown(report.targetId);
    setTakingDown(null);
    if (ok) {
      dismissReport(report.id);
      setReports(getAllReports());
    } else {
      setTakedownStatus(s => ({ ...s, [report.id]: 'fail' }));
    }
  };

  if (!isAdmin(identity.publicKey)) {
    return (
      <div style={{ padding: SPACING.xl, color: COLORS.textMuted, fontFamily: '"IBM Plex Mono", monospace', fontSize: '12px' }}>
        Not authorized.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: SPACING.md, borderBottom: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', gap: SPACING.md, flexShrink: 0 }}>
        <button
          onClick={onBack}
          style={{ background: 'none', border: 'none', color: COLORS.highlight, cursor: 'pointer', fontFamily: '"IBM Plex Mono", monospace', fontSize: '13px', padding: 0 }}
        >
          ← back
        </button>
        <span style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '13px', color: COLORS.text, fontWeight: 700 }}>
          Reported content
        </span>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: SPACING.lg }}>
        <p style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '10px', color: COLORS.textMuted, marginBottom: SPACING.lg, lineHeight: 1.6 }}>
          Reports are submitted by users flagging public content (posts, videos, listings, events, communities).
          This queue is for triage only — actual takedown happens via the relay's authenticated admin API,
          since the client has no authority to remove content it doesn't own. DMs are end-to-end encrypted
          and never appear here — see the admin/moderation design spec for the locked privacy guarantee.
        </p>

        {reports.length === 0 ? (
          <p style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '11px', color: COLORS.textMuted }}>
            No reports.
          </p>
        ) : (
          reports.map(r => (
            <div key={r.id} style={{ border: `1px solid ${COLORS.border}`, padding: SPACING.md, marginBottom: SPACING.md, backgroundColor: COLORS.panel }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: SPACING.xs }}>
                <span style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '11px', fontWeight: 700, color: COLORS.accent }}>
                  {REASON_LABELS[r.reason]}
                </span>
                <span style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '9px', color: COLORS.textMuted }}>
                  {new Date(r.createdAt).toLocaleString()}
                </span>
              </div>
              <p style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '10px', color: COLORS.text, margin: `0 0 ${SPACING.xs} 0` }}>
                target: {r.targetType} · {r.targetId.slice(0, 16)}…
              </p>
              <p style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '10px', color: COLORS.textMuted, margin: `0 0 ${SPACING.sm} 0` }}>
                reported by {pubkeyShortHash(r.reporterPubkey)}
              </p>
              <div style={{ display: 'flex', gap: SPACING.sm, alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={() => handleTakedown(r)}
                  disabled={takingDown === r.id}
                  style={{ background: COLORS.accent, border: 'none', color: '#fff', cursor: 'pointer', padding: `${SPACING.xs} ${SPACING.md}`, fontFamily: '"IBM Plex Mono", monospace', fontSize: '10px', opacity: takingDown === r.id ? 0.6 : 1 }}
                >
                  {takingDown === r.id ? '...' : 'takedown'}
                </button>
                <button
                  onClick={() => handleDismiss(r.id)}
                  style={{ background: 'none', border: `1px solid ${COLORS.border}`, color: COLORS.textMuted, cursor: 'pointer', padding: `${SPACING.xs} ${SPACING.md}`, fontFamily: '"IBM Plex Mono", monospace', fontSize: '10px' }}
                >
                  dismiss
                </button>
                {takedownStatus[r.id] === 'fail' && (
                  <span style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '9px', color: COLORS.accent }}>
                    takedown failed — check relay
                  </span>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
